import React, { useCallback, useEffect, useState, useRef } from "react"
import { useTranslation } from "react-i18next"
import { ArrowRight, CloseLarge } from "@/skylticons"
import { ReactComponent as ConsumerImg } from "@/images/layout/modal/consumer.svg"
import { ReactComponent as BusinessImg } from "@/images/layout/modal/business.svg"
import { ReactComponent as CookieJar } from "@/images/layout/modal/cookie-jar.svg"
import cookieSideImg from "@/images/layout/modal/acrylic-cookie.png"
import cn from "classnames"
import env from "@/common/env"
import { useMutation } from "@apollo/client"
import { getFragment, graphql } from "@/api"
import CookieChoices, {
  CookieConsentFragment,
  currentCookieConsentDoc,
  cookieConsentCreateDoc,
} from "@/cookies/components/cookie_choices"
import type { CurrentCookieConsentQuery, AbTestInput } from "@/api/graphql"
import Cookies from "js-cookie"
import { pushConsentToDataLayer, transitionTime } from "@/common/util"
import { Dialog } from "@headlessui/react"
import { motion, AnimatePresence } from "framer-motion"
import { isTablet } from "@/common/util"
import useMedia from "@/common/hooks/use_media"
import { pushToDataLayer } from "@/common/util"

const setClientRoleDoc = graphql(`
  mutation SetClientRole($role: String!) {
    setClientRole(role: $role) {
      success
    }
  }
`)

const updateAbTestVariablesDoc = graphql(`
  mutation UpdateAbTestVariables($input: AbTestInput!) {
    updateAbTestVariables(input: $input) {
      success
    }
  }
`)

interface VariantAProps {
  modalNr: number
  updateConsumer: () => void
  updateBusiness: () => void
  updateCookie: (functional: boolean, analytics: boolean, marketing: boolean) => void
  cookieLoading: boolean
  roleLoading: boolean
  updateSettingsClick: () => void
  consumerActive: boolean
  businessActive: boolean
  consented: boolean
}

const VariantA: React.FC<VariantAProps> = ({
  modalNr,
  updateConsumer,
  updateBusiness,
  updateCookie,
  cookieLoading,
  roleLoading,
  updateSettingsClick,
  consumerActive,
  businessActive,
  consented,
}) => {
  const { t } = useTranslation()

  return modalNr === 2 ? (
    <div className="flex flex-col lg:flex-row justify-between w-full">
      <button
        className={cn("flex items-center p-5 rounded-xl h-24 lg:h-[104px] mb-2 lg:mb-0 lg:mr-4 w-full border", {
          "bg-blue-200 border-primary": consented,
          "bg-secondary onlyHover:hover:bg-white-3 onlyHover:hover:border-ink-50": !consented,
          "pointer-events-none": cookieLoading,
        })}
        onClick={() => updateCookie(true, true, true)}
        id="accept-cookies"
        data-gtm="modal-accept-cookies-a"
        type="button"
      >
        <CookieJar className="w-9 h-12 lg:w-12 lg:h-16" />
        <div className="flex flex-col items-baseline ml-4">
          <span className="heading-four w-auto m-0">{t("cookies.accept_all")}</span>
          <span className="link-button flex items-center icon-right text-sm font-medium m-0">
            {t("misc.select")}
            <ArrowRight className="w-6 h-6" />
          </span>
        </div>
      </button>
      <div className="flex w-full flex-col gap-2">
        <button
          className="h-12 py-0 px-4 rounded text-base leading-5 font-semibold border-1 border-white-20 bg-secondary text-ink-100 onlyHover:hover:bg-secondary-light onlyHover:hover:text-ink-85"
          onClick={() => updateCookie(false, false, false)}
          id="only-necessary"
          data-gtm="only-necessary-a"
          type="button"
        >
          {t("cookies.only_necessary")}
        </button>
        <button
          onClick={updateSettingsClick}
          className="h-12 py-0 px-4 rounded text-base leading-5 font-semibold border-1 border-white-20 bg-secondary text-ink-100 onlyHover:hover:bg-secondary-light onlyHover:hover:text-ink-85"
          id="cookie-settings"
          data-gtm="modal-settings-a"
          type="button"
        >
          {t("toolbox.settings")}
        </button>
      </div>
    </div>
  ) : (
    <div className="role-switch flex justify-between w-full flex-col lg:flex-row">
      <button
        className={cn(
          "consumer relative flex lg:flex-col items-center justify-start lg:justify-center p-4 cursor-pointer border rounded-xl w-full h-24 mb-2 mr-0 lg:mr-4 lg:mb-0 lg:w-[280px] lg:h-[196px]",
          {
            "bg-blue-200 border-primary": consumerActive,
            "bg-secondary onlyHover:hover:bg-white-3 onlyHover:hover:border-ink-50": !consumerActive,
            "pointer-events-none": roleLoading,
          }
        )}
        onClick={() => updateConsumer()}
        data-gtm="modal-consumer-a"
        type="button"
      >
        <ConsumerImg className="w-12 h-12 mr-4 mb-0 lg:w-16 lg:h-16 lg:mb-2 lg:mr-0" />
        <div className="flex flex-col justify-start lg:items-center">
          <span className="heading-four lg:heading-three !w-auto !mb-0">{t("customer.consumer")}</span>
          <p className="paragraph-2xs w-auto mb-0 lg:mb-1 absolute right-4 bottom-4 lg:static">
            {t("rolemodal.prices_vat")}
          </p>
          <span className="link-button flex items-center icon-right text-sm lg:text-base font-medium m-0">
            {t("misc.select")}
            <ArrowRight className="w-6 h-6 lg:ml-1" />
          </span>
        </div>
      </button>
      <button
        className={cn(
          "business relative flex lg:flex-col items-center justify-start lg:justify-center p-4 cursor-pointer border rounded-xl w-full h-24 lg:w-[280px] lg:h-[196px]",
          {
            "bg-blue-200 border-primary": businessActive,
            "bg-secondary onlyHover:hover:bg-white-3 onlyHover:hover:border-ink-50": !businessActive,
            "pointer-events-none": roleLoading,
          }
        )}
        onClick={() => updateBusiness()}
        data-gtm="modal-business-a"
        type="button"
      >
        <BusinessImg className="w-12 h-12 mr-4 mb-0 lg:w-16 lg:h-16 lg:mb-2 lg:mr-0" />
        <div className="flex flex-col justify-start lg:items-center">
          <span className="heading-four lg:heading-three !w-auto !mb-0">{t("customer.business")}</span>
          <p className="paragraph-2xs w-auto mb-0 lg:mb-1 absolute right-4 bottom-4 lg:static">
            {t("rolemodal.prices_novat")}
          </p>
          <span className="link-button flex items-center icon-right text-sm lg:text-base font-medium m-0">
            {t("misc.select")}
            <ArrowRight className="w-6 h-6 lg:ml-1" />
          </span>
        </div>
      </button>
    </div>
  )
}

interface VariantBProps {
  modalNr: number
  updateConsumer: () => void
  updateBusiness: () => void
  updateCookie: (functional: boolean, analytics: boolean, marketing: boolean) => void
  cookieLoading: boolean
  roleLoading: boolean
  updateSettingsClick: () => void
}

const VariantB: React.FC<VariantBProps> = ({
  modalNr,
  updateConsumer,
  updateBusiness,
  cookieLoading,
  roleLoading,
  updateCookie,
  updateSettingsClick,
}) => {
  const { t } = useTranslation()

  return modalNr === 2 ? (
    <div className="role-switch-b flex flex-col w-full sm:flex-row">
      <button
        onClick={() => updateConsumer()}
        disabled={roleLoading}
        className="consumer flex sm:flex-col items-center sm:justify-center w-full h-12 sm:h-14 mb-2 cursor-pointer bg-primary rounded-[4px] px-4 sm:mb-0 sm:mr-2 onlyHover:hover:bg-primary-dark disabled:opacity-50 disabled:no-pointer-events"
        data-gtm="modal-consumer-b"
        type="button"
      >
        <span className="heading-five mb-0 text-white">{t("customer.consumer")}</span>
        <p className="paragraph-2xs mb-0 text-white-3 text-right sm:text-start">{t("rolemodal.prices_vat")}</p>
      </button>
      <button
        onClick={() => updateBusiness()}
        disabled={roleLoading}
        className="business flex sm:flex-col items-center sm:justify-center w-full h-12 sm:h-14 cursor-pointer bg-primary rounded-[4px] px-4 onlyHover:hover:bg-primary-dark disabled:opacity-50 disabled:no-pointer-events"
        data-gtm="modal-business-b"
        type="button"
      >
        <span className="heading-five mb-0 text-white">{t("customer.business")}</span>
        <p className="paragraph-2xs mb-0 text-white-3 text-right sm:text-start">{t("rolemodal.prices_novat")}</p>
      </button>
    </div>
  ) : (
    <div className="flex flex-col w-full sm:flex-row">
      <button
        id="accept-cookies"
        className="button button-primary button-large mb-2 w-full sm:w-auto sm:mr-2 sm:mb-0 disabled:opacity-50 disabled:no-pointer-events"
        onClick={() => updateCookie(true, true, true)}
        disabled={cookieLoading}
        data-gtm="modal-accept-cookies-b"
        type="button"
      >
        {t("cookies.accept_all")}
      </button>
      <button
        id="only-necessary"
        className="button button-primary button-large mb-2 w-full sm:w-auto sm:mr-2 sm:mb-0 disabled:opacity-50 disabled:no-pointer-events ml-0"
        onClick={() => updateCookie(false, false, false)}
        disabled={cookieLoading}
        data-gtm="only-necessary-b"
        type="button"
      >
        {t("cookies.only_necessary")}
      </button>
      <button
        id="cookie-settings"
        className="button button-secondary button-large !m-0 w-full sm:w-auto"
        onClick={updateSettingsClick}
        data-gtm="modal-settings-b"
        type="button"
      >
        {t("toolbox.settings")}
      </button>
    </div>
  )
}

interface RoleAndCookieModalProps {
  showCookieModal: boolean
  showRoleModal: boolean
  variantB: boolean
  privacyURL: string
  business: boolean
}

const RoleAndCookieModal: React.FC<RoleAndCookieModalProps> = ({
  showCookieModal,
  showRoleModal,
  variantB,
  privacyURL,
  business,
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(true)

  const [consumerActive, setConsumerActive] = useState(false)
  const [businessActive, setBusinessActive] = useState(false)
  const [consented, setConsented] = useState(false)
  const [settingsActive, setSettingsActive] = useState(false)

  const [cookieConsentCreateMutation, cookieConsentCreateMutationResult] = useMutation(cookieConsentCreateDoc)
  const [roleMutation, roleMutationResult] = useMutation(setClientRoleDoc)
  const [abTestMutation] = useMutation(updateAbTestVariablesDoc)

  const enableSecondModal = variantB ? showRoleModal : showCookieModal
  const showOnlySecondModal = variantB ? !showCookieModal : !showRoleModal

  const [modalNr, setModalNr] = useState(showOnlySecondModal ? 2 : 1)

  const cookieModal = variantB ? showCookieModal && modalNr === 1 : showCookieModal && modalNr === 2
  const roleModal = variantB ? showRoleModal && modalNr === 2 : showRoleModal && modalNr === 1

  const updateTestVariable = useCallback(
    async (input: AbTestInput) => {
      try {
        await abTestMutation({ variables: { input } })
      } catch (error) {
        console.error(error.message)
      }
    },
    [abTestMutation]
  )

  const pushGaEvent = useCallback(() => {
    pushToDataLayer({
      event: "user-role",
      eventAction: "click",
      eventLabel: business || businessActive ? "business" : "consumer",
    })
  }, [businessActive, business])

  const toggleModal = useCallback(
    (close?: boolean) => {
      if (variantB) {
        if (showRoleModal && modalNr === 1) {
          setModalNr(2)

          return
        }
      } else {
        if (showCookieModal && modalNr === 1) {
          setModalNr(2)
          setConsumerActive(true)

          return
        }
      }

      setOpen(v => !v)

      if (close) {
        window.location.reload()
      }
    },
    [modalNr, showCookieModal, variantB, showRoleModal]
  )

  const handleClientRole = useCallback(
    async (role: string, closed?: boolean) => {
      if ((variantB && modalNr === 1) || cookieModal) {
        return
      }

      if (closed) {
        try {
          await updateTestVariable({ roleModalClose: true })
        } catch (error) {
          console.error(error.message)
        }
      }

      if (role === "consumer") {
        setConsumerActive(true)
      }

      try {
        await roleMutation({ variables: { role: role } })
      } catch (error) {
        console.error(error.message)
      }

      enableSecondModal && modalNr === 1 ? setModalNr(2) : toggleModal(true)
    },
    [roleMutation, toggleModal, enableSecondModal, modalNr, variantB, cookieModal, updateTestVariable]
  )

  const handleSettingsClose = useCallback(() => {
    if (variantB) {
      setModalNr(1)
    } else {
      enableSecondModal ? setModalNr(2) : setModalNr(1)
    }
  }, [variantB, enableSecondModal])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault()

        if (open) {
          if (modalNr === 3) {
            handleSettingsClose()
          } else if (!cookieModal) {
            handleClientRole("consumer", true)
          }
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown, { capture: true })

    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true })
    }
  }, [cookieModal, handleClientRole, open, modalNr, handleSettingsClose])

  const handleUnloadEvent = useCallback(async () => {
    try {
      await updateTestVariable({ left: true })
    } catch (error) {
      console.error(error.message)
    }
  }, [updateTestVariable])

  useEffect(() => {
    if (modalNr === 2 || businessActive || consumerActive || settingsActive) {
      return
    }

    window.addEventListener("beforeunload", handleUnloadEvent)

    return () => {
      window.removeEventListener("beforeunload", handleUnloadEvent)
    }
  }, [handleUnloadEvent, modalNr, businessActive, consumerActive, settingsActive])

  const updateSettingsClick = useCallback(async () => {
    setSettingsActive(true)
    setModalNr(3)

    await updateTestVariable({ cookieSettings: true })
  }, [updateTestVariable])

  const updateConsumer = useCallback(() => {
    setConsumerActive(true)
    handleClientRole("consumer")
  }, [handleClientRole])

  const updateBusiness = useCallback(() => {
    setBusinessActive(true)
    handleClientRole("business")
  }, [handleClientRole])

  const handleOverlayClose = useCallback(() => {
    if (modalNr === 3) {
      handleSettingsClose()
    } else {
      handleClientRole("consumer", true)
    }
  }, [modalNr, handleSettingsClose, handleClientRole])

  useEffect(() => {
    if ((consumerActive || businessActive || (!variantB && consented)) && !open) {
      pushGaEvent()
    }
  }, [consumerActive, businessActive, pushGaEvent, open, variantB, consented])

  const saveConsent = useCallback(
    async (functional: boolean, analytics: boolean, marketing: boolean) => {
      const result = await cookieConsentCreateMutation({
        variables: { functional: functional, analytics: analytics, marketing: marketing },
        update: (client, { data }) => {
          if (data?.cookieConsentCreate?.consent) {
            client.writeQuery<CurrentCookieConsentQuery>({
              query: currentCookieConsentDoc,
              data: { currentCookieConsent: data.cookieConsentCreate.consent },
            })
          }
        },
      })

      if (variantB && modalNr === 1) {
        setModalNr(2)
      }

      const consent = getFragment(CookieConsentFragment, result.data?.cookieConsentCreate?.consent)

      if (consent?.id) {
        Cookies.set("cookies_consented", consent.id, { expires: 180 }) // 6 mo

        pushConsentToDataLayer(functional, analytics, marketing)
      }

      toggleModal(true)
    },
    [cookieConsentCreateMutation, toggleModal, modalNr, variantB]
  )

  const updateCookie = useCallback(
    async (functional: boolean, analytics: boolean, marketing: boolean) => {
      setConsented(functional && analytics && marketing)

      if (!functional && !analytics && !marketing) {
        await updateTestVariable({ onlyNecessary: true })
      }

      saveConsent(functional, analytics, marketing)
    },
    [saveConsent, updateTestVariable]
  )

  const domain = env.locale.slice(0, 2)
  const userRoleSideImgs = import.meta.glob("@/images/layout/modal/user-role-welcome-sign--*.png", { eager: true })
  const userRoleSideImg = userRoleSideImgs[`/images/layout/modal/user-role-welcome-sign--${domain}.png`] as any

  const cookieLoading = cookieConsentCreateMutationResult.loading
  const roleLoading = roleMutationResult.loading
  const modalImg = variantB ? false : cookieModal ? cookieSideImg : userRoleSideImg.default
  const modalTitle = cookieModal
    ? t("cookies.modal_heading", { domain: t("site.domain") })
    : variantB
    ? t("rolemodal.site_select")
    : t("rolemodal.heading")
  const modalPromise = cookieModal ? t("cookies.modal_description") : t("rolemodal.description")

  const focusRef = useRef(null)
  const enableScroll = useMedia("(max-height: 620px)")

  const ModalContent = () => (
    <>
      {!cookieModal && (
        <a
          className={cn("close button button-large button-subtle icon-only mb-0 absolute", {
            "h-10 w-10 right-4 top-4": variantB,
            "right-2 top-2 lg:right-4 lg:top-4": !variantB,
          })}
          onClick={() => handleClientRole("consumer", true)}
          data-gtm={variantB ? "modal-role-close-button-b" : "modal-role-close-button-a"}
        >
          <CloseLarge />
        </a>
      )}
      {modalImg && !variantB && (
        <div className="hidden bg-secondary rounded-l-2xl w-80 items-center lg:flex">
          <img className="max-w-xs" src={modalImg} alt="" />
        </div>
      )}
      <div
        className={cn("w-full px-4 py-6", {
          "sm:p-6": variantB,
          "lg:px-8 lg:py-10": !variantB,
          "max-h-[calc(100vh-32px)] overflow-y-auto flex flex-col": enableScroll,
        })}
      >
        <Dialog.Title
          as="h4"
          className={cn("focus:outline-none", {
            "mb-4": variantB && modalNr === 2,
            "lg:heading-three": !variantB,
            "!w-[calc(100%-42px)]": !cookieModal,
          })}
          ref={focusRef}
          tabIndex={0}
        >
          {modalTitle}
        </Dialog.Title>
        <Dialog.Description as="div">
          <>
            <p className={cn("text-sm", { "mb-0": cookieModal, hidden: roleModal && variantB })}>{modalPromise}</p>
            {cookieModal && (
              <>
                <ul className="list-disc list-inside ml-2 mb-2">
                  <li className="!mb-0 marker:mr-1 marker:text-xs">{t("cookies.modal_use_chat")}</li>
                  <li className="!mb-0 marker:mr-1 marker:text-xs">{t("cookies.modal_use_analytics")}</li>
                  <li className="!mb-0 marker:mr-1 marker:text-xs">{t("cookies.modal_use_marketing")}</li>
                </ul>
                <a
                  className="block mb-6 text-sm text-black underline font-medium w-auto"
                  href={`${privacyURL}#cookie-paragraph`}
                  onClick={() => setSettingsActive(true)}
                  data-gtm={variantB ? "modal-settings-b" : "modal-settings-a"}
                >
                  {t("cookies.read_privacy")}
                </a>
              </>
            )}
          </>
        </Dialog.Description>

        <div className="modal-actions">
          {variantB ? (
            <VariantB
              modalNr={modalNr}
              updateConsumer={updateConsumer}
              updateBusiness={updateBusiness}
              updateCookie={updateCookie}
              cookieLoading={cookieLoading}
              roleLoading={roleLoading}
              updateSettingsClick={updateSettingsClick}
            />
          ) : (
            <VariantA
              modalNr={modalNr}
              updateConsumer={updateConsumer}
              updateBusiness={updateBusiness}
              updateCookie={updateCookie}
              cookieLoading={cookieLoading}
              roleLoading={roleLoading}
              updateSettingsClick={updateSettingsClick}
              consumerActive={consumerActive}
              businessActive={businessActive}
              consented={consented}
            />
          )}
        </div>
      </div>
    </>
  )

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            className={cn("fixed inset-0 bg-black z-[2100]", { "bg-opacity-50": !variantB, "bg-opacity-30": variantB })}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: transitionTime(0.2), ease: "easeInOut" }}
          />
        )}
      </AnimatePresence>

      <Dialog
        open={open}
        as="div"
        className="role-cookie-modal relative z-[2200]"
        onClose={handleOverlayClose}
        initialFocus={focusRef}
      >
        <div
          className={cn("fixed overlay inset-0 flex items-end", {
            "sm:items-center justify-center": !variantB,
          })}
          onClick={handleOverlayClose}
        >
          <AnimatePresence mode="wait">
            {modalNr === 1 && (
              <motion.div
                className={cn("first-modal flex items-end sm:items-center justify-center w-full h-full")}
                key="first-modal"
                initial={{ translateY: 20, opacity: 0 }}
                animate={{ translateY: 0, opacity: 1 }}
                exit={{ translateY: -20, opacity: 0 }}
                transition={{ duration: transitionTime(0.2), ease: "easeInOut" }}
              >
                <Dialog.Panel
                  className={cn("flex rounded-2xl bg-white shadow-xl mx-3 mb-4 sm:m-0", {
                    "sm:left-4 sm:bottom-4 w-screen max-w-[600px] relative sm:absolute": variantB,
                    relative: !variantB,
                    "p-0 flex-row w-full max-w-[480px] lg:max-w-[960px]": modalImg,
                    "flex-col": !modalImg,
                    "overflow-auto": enableScroll,
                  })}
                >
                  <ModalContent />
                </Dialog.Panel>
              </motion.div>
            )}
            {modalNr === 2 && (
              <motion.div
                className={cn("second-modal flex items-end justify-center w-full h-full sm:items-center")}
                key="second-modal"
                initial={{ translateY: 20, opacity: 0 }}
                animate={{ translateY: 0, opacity: 1 }}
                exit={{ translateY: -20, opacity: 0 }}
                transition={{ duration: transitionTime(0.2), ease: "easeInOut" }}
              >
                <Dialog.Panel
                  className={cn("flex rounded-2xl bg-white shadow-xl z-[1100] mx-3 mb-4 sm:m-0", {
                    "sm:left-4 sm:bottom-4 w-screen max-w-[600px] relative sm:absolute": variantB,
                    relative: !variantB,
                    "p-0 flex-row w-full max-w-[480px] lg:max-w-[960px]": modalImg,
                    "flex-col": !modalImg,
                    "overflow-auto": enableScroll,
                  })}
                >
                  <ModalContent />
                </Dialog.Panel>
              </motion.div>
            )}
            {modalNr === 3 && (
              <>
                {isTablet() ? (
                  <motion.div
                    className="third-modal flex items-center justify-center w-full h-full"
                    key="third-modal"
                    initial={{ translateY: "100vh" }}
                    animate={{ translateY: 0 }}
                    exit={{ translateY: "100vh" }}
                    transition={{ duration: transitionTime(0.3), ease: "easeInOut" }}
                  >
                    <CookieChoices
                      privacyURL={privacyURL}
                      show
                      handleClose={handleSettingsClose}
                      wrapper={false}
                      updateCookie={updateCookie}
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    className="third-modal flex items-center justify-center w-full h-full"
                    key="third-modal"
                    initial={{ translateX: -480 }}
                    animate={{ translateX: 0 }}
                    exit={{ translateX: -480 }}
                    transition={{ duration: transitionTime(0.3), ease: "easeInOut" }}
                  >
                    <CookieChoices
                      privacyURL={privacyURL}
                      show
                      handleClose={handleSettingsClose}
                      wrapper={false}
                      updateCookie={updateCookie}
                    />
                  </motion.div>
                )}
              </>
            )}
          </AnimatePresence>
        </div>
      </Dialog>
    </>
  )
}

export default RoleAndCookieModal
